import React, { useState, useEffect } from 'react';
import { http } from '../../lib/http';

const CancellationRequestsTab = ({ apiBase, setMessage, setError, onCountChange }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState({});

    const loadRequests = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await http.get(
                `${apiBase}/cancellation-requests`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = response.data.data?.requests || [];
            setRequests(data);
            if (onCountChange) onCountChange(data.filter(r => r.status === 'pending_attention').length);
        } catch (error) {
            console.error('Failed to load cancellation requests:', error);
            setError(error.response?.data?.detail || 'Failed to load cancellation requests');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRequests();
    }, []);

    const deleteRequest = async (requestId) => {
        if (!window.confirm('Delete this cancellation request permanently?')) return;

        const key = `${requestId}-delete`;
        setProcessing(prev => ({ ...prev, [key]: true }));

        try {
            await http.delete(`${apiBase}/cancellation-requests/${requestId}`);
            setMessage('Cancellation request deleted successfully');
            await loadRequests();
        } catch (error) {
            console.error('Failed to delete cancellation request:', error);
            setError(error.response?.data?.detail || 'Failed to delete cancellation request');
        } finally {
            setProcessing(prev => ({ ...prev, [key]: false }));
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-CA');
    };

    if (loading) {
        return (
            <div className="bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-6 rounded-lg min-h-[400px]">
                <div className="bg-white p-8 rounded-xl shadow-lg text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-red-500 mx-auto mb-4"></div>
                    <p className="text-gray-700 text-base font-semibold">Loading cancellation requests...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg">
            {/* Header */}
            <div className="bg-gradient-to-r from-black to-gray-900 rounded-xl p-6 shadow-lg border-2 border-red-500">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">&#10060;</span>
                        <h3 className="text-2xl font-bold text-white">Cancellation Requests</h3>
                        <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                            {requests.length}
                        </span>
                    </div>
                    <button
                        onClick={loadRequests}
                        className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white px-6 py-2 rounded-lg text-sm font-semibold shadow transition-all hover:scale-105"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {requests.length === 0 ? (
                <div className="bg-white p-8 rounded-xl shadow-lg text-center border-2 border-red-500">
                    <svg className="mx-auto h-16 w-16 text-green-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">No Cancellation Requests</h4>
                    <p className="text-gray-600 text-sm">No pending cancellation requests found</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {requests.map((request) => (
                        <div key={request.id} className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 border-2 border-red-500 ring-2 ring-red-200">
                            {/* Card Header */}
                            <div className="flex justify-between items-start mb-4 pb-3 border-b border-gray-200">
                                <div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-1">{request.customer_name}</h4>
                                    <p className="text-sm text-gray-600 mb-1">{request.customer_email}</p>
                                    <p className="text-sm text-gray-800 font-medium mb-2">
                                        {request.customer_name} requested to cancel their subscription
                                    </p>
                                    <div className="flex flex-wrap gap-2 text-xs">
                                        <span className="px-3 py-1 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-full font-semibold">
                                            CANCELLATION
                                        </span>
                                        <span className={`px-3 py-1 rounded-full font-semibold ${
                                            request.status === 'pending_attention' ? 'bg-red-100 text-red-700' :
                                            request.status === 'resolved' ? 'bg-green-100 text-green-700' :
                                            'bg-yellow-100 text-yellow-700'
                                        }`}>
                                            {request.status ? request.status.replace(/_/g, ' ').toUpperCase() : 'N/A'}
                                        </span>
                                        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full font-semibold flex items-center gap-1">
                                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            {formatDate(request.created_at)}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex space-x-2 items-center">
                                    {request.status === 'pending_attention' && (
                                        <span className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full border border-red-300 shadow-sm">
                                            Pending Review
                                        </span>
                                    )}
                                    <button
                                        onClick={() => deleteRequest(request.id)}
                                        disabled={processing[`${request.id}-delete`]}
                                        className="bg-gradient-to-r from-gray-600 to-gray-500 hover:from-gray-700 hover:to-gray-600 disabled:from-gray-400 disabled:to-gray-500 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow transition-all hover:scale-105"
                                    >
                                        {processing[`${request.id}-delete`] ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </div>

                            {/* Alert Banner */}
                            {request.status === 'pending_attention' && (
                                <div className="mt-3 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-red-800">Action Required: Customer Cancellation</h3>
                                            <p className="mt-2 text-sm text-red-700">
                                                This customer has requested to cancel their plan. Please review their reason, contact them if necessary, and manually execute the cancellation process in the Customer Management tab.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Cancellation Details */}
                            {request.details && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    {/* Current Plan Info */}
                                    {(request.details.current_meal_plan_type || request.details.current_plan_key) && (
                                        <div className="mb-3 bg-gradient-to-br from-gray-900 to-black rounded-lg p-3 border border-gray-700">
                                            <h6 className="font-semibold text-sm text-white mb-2 flex items-center gap-2">
                                                <span>&#128203;</span> Current Plan
                                            </h6>
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                {request.details.current_meal_plan_type && (
                                                    <div><span className="text-red-400 font-semibold">Type:</span> <span className="text-gray-300 ml-1">{request.details.current_meal_plan_type}</span></div>
                                                )}
                                                {request.details.current_meal_plan_term && (
                                                    <div><span className="text-red-400 font-semibold">Term:</span> <span className="text-gray-300 ml-1">{request.details.current_meal_plan_term}</span></div>
                                                )}
                                                {request.details.current_plan_key && (
                                                    <div><span className="text-red-400 font-semibold">Plan:</span> <span className="text-gray-300 ml-1">{request.details.current_plan_key}</span></div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Cancellation Reason */}
                                    {request.details.cancellation_reason && (
                                        <div className="mb-3 bg-red-50 rounded-lg p-3 border border-red-200">
                                            <h6 className="font-semibold text-sm text-red-800 mb-1 flex items-center gap-2">
                                                <span>&#128172;</span> Cancellation Reason
                                            </h6>
                                            <p className="text-sm text-red-700">{request.details.cancellation_reason}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CancellationRequestsTab;
